import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import NewTaskPage from './NewTaskPage';
import { api } from '../api/client';
import { useRegisterRefresh } from '../context/PullToRefreshContext';
import TaskCard from '../components/TaskCard';

const TABS = [
  { key: 'mine', label: 'Benim İşlerim' },
  { key: 'delegated', label: 'Devrettiklerim' },
  { key: 'all', label: 'Tüm İşler' },
];

const STATUS_FILTERS = [
  { key: 'all', label: 'Tümü' },
  { key: 'overdue', label: 'Geciken' },
  { key: 'in_progress', label: 'Devam Eden' },
  { key: 'completed', label: 'Tamamlandı' },
];

const CUSTOMER_TYPE_FILTERS = [
  { key: 'all', label: 'Tümü' },
  { key: 'sanayici', label: 'Sanayici' },
  { key: 'boru', label: 'Boru' },
  { key: 'bayi', label: 'Bayi' },
  { key: 'none', label: 'Genel İş' },
];

const TASK_LIST_STATE_KEY = 'taskListState';
const VALID_TAB_KEYS = new Set(TABS.map(t => t.key));
const VALID_STATUS_KEYS = new Set(STATUS_FILTERS.map(f => f.key));
const VALID_CUSTOMER_TYPE_KEYS = new Set(CUSTOMER_TYPE_FILTERS.map(f => f.key));

/** Sekmeye göre karttaki “sorumlu” filtresinde kullanılacak isim (Devrettiklerim’de delege). */
function getAssigneeFilterLabel(task, tab) {
  if (tab === 'delegated') {
    return task.delegated_to_name || null;
  }
  return task.assignee_name || null;
}

export default function TaskListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const newTaskOpen = searchParams.get('new') === '1';

  const openNewTaskModal = () => {
    sessionStorage.setItem('newTaskModalOpen', '1');
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('new', '1');
        return next;
      },
      { replace: true }
    );
  };

  const closeNewTaskModal = () => {
    sessionStorage.removeItem('newTaskModalOpen');
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('new');
        return next;
      },
      { replace: true }
    );
  };

  useEffect(() => {
    if (sessionStorage.getItem('newTaskModalOpen') === '1') {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('new', '1');
          return next;
        },
        { replace: true }
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [activeTab, setActiveTab] = useState('mine');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [customerTypeFilter, setCustomerTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  const skipPersistAfterMountRef = useRef(true);
  const scrollRestoredRef = useRef(false);
  const tasksLoadedRef = useRef(false);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(TASK_LIST_STATE_KEY);
      if (!raw) return;
      const s = JSON.parse(raw);
      if (!s || typeof s !== 'object') return;
      if (VALID_TAB_KEYS.has(s.activeTab)) setActiveTab(s.activeTab);
      if (VALID_STATUS_KEYS.has(s.statusFilter)) setStatusFilter(s.statusFilter);
      if (VALID_CUSTOMER_TYPE_KEYS.has(s.customerTypeFilter)) setCustomerTypeFilter(s.customerTypeFilter);
      if (typeof s.searchQuery === 'string') setSearchQuery(s.searchQuery);
      if (s.assigneeFilter === 'all' || (typeof s.assigneeFilter === 'string' && s.assigneeFilter.length > 0)) {
        setAssigneeFilter(s.assigneeFilter);
      }
      if (typeof s.currentPage === 'number' && Number.isInteger(s.currentPage) && s.currentPage >= 1) {
        setCurrentPage(s.currentPage);
      }
    } catch {
      // ignore invalid JSON or storage errors
    }
  }, []);

  useEffect(() => {
    if (skipPersistAfterMountRef.current) {
      skipPersistAfterMountRef.current = false;
      return;
    }
    try {
      sessionStorage.setItem(
        TASK_LIST_STATE_KEY,
        JSON.stringify({
          activeTab,
          statusFilter,
          customerTypeFilter,
          searchQuery,
          assigneeFilter,
          currentPage,
        })
      );
    } catch {
      // ignore quota / private mode
    }
  }, [activeTab, statusFilter, customerTypeFilter, searchQuery, assigneeFilter, currentPage]);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.getTasks({ tab: activeTab });
      setTasks(Array.isArray(data) ? data : data.results || []);
      tasksLoadedRef.current = true;
    } catch {
      setError('İşler yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  // Scroll pozisyonunu tasks render edildikten sonra restore et
  useEffect(() => {
    if (!tasksLoadedRef.current || scrollRestoredRef.current) return;
    scrollRestoredRef.current = true;
    const saved = sessionStorage.getItem('scrollPos_/satis');
    if (saved) {
      const el = document.getElementById('main-scroll');
      if (el) el.scrollTop = +saved;
    }
  }, [tasks]);
  useRegisterRefresh(fetchTasks);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  // Tab değişince filtreleri sıfırla
  const handleTabChange = (key) => {
    setActiveTab(key);
    setStatusFilter('all');
    setCustomerTypeFilter('all');
    setSearchQuery('');
    setAssigneeFilter('all');
    setCurrentPage(1);
  };

  // Tasklardan benzersiz sorumlular (Devrettiklerim: delege adı)
  const assigneeOptions = useMemo(() => {
    const map = new Map();
    tasks.forEach(t => {
      const label = getAssigneeFilterLabel(t, activeTab);
      if (label) map.set(label, label);
    });
    return [...map.values()].sort();
  }, [tasks, activeTab]);

  const filteredTasks = useMemo(() => {
    let result = tasks;

    // Durum filtresi
    if (statusFilter !== 'all') {
      result = result.filter(t => {
        const isOverdue = t.is_overdue || t.status === 'overdue';
        if (statusFilter === 'overdue') return isOverdue;
        if (statusFilter === 'in_progress') return t.status === 'in_progress' && !isOverdue;
        if (statusFilter === 'completed') return t.status === 'completed';
        return true;
      });
    }

    // Müşteri tipi filtresi
    if (customerTypeFilter !== 'all') {
      result = result.filter(t => {
        if (customerTypeFilter === 'none') return !t.customer_type;
        return t.customer_type === customerTypeFilter;
      });
    }

    // Sorumlu filtresi (Devrettiklerim: delegeye göre)
    if (assigneeFilter !== 'all') {
      result = result.filter(t => getAssigneeFilterLabel(t, activeTab) === assigneeFilter);
    }

    // Metin araması — kod, başlık, müşteri adı
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(t =>
        t.code?.toLowerCase().includes(q) ||
        t.title?.toLowerCase().includes(q) ||
        t.customer_name?.toLowerCase().includes(q)
      );
    }

    return result;
  }, [tasks, activeTab, statusFilter, customerTypeFilter, assigneeFilter, searchQuery]);

  const activeFilterCount = (statusFilter !== 'all' ? 1 : 0) + (customerTypeFilter !== 'all' ? 1 : 0) +
    (assigneeFilter !== 'all' ? 1 : 0) + (searchQuery.trim() ? 1 : 0);

  const PAGE_SIZE = 5;

  // Durum filtresi sayıları — müşteri tipi filtresini uygula, durum filtresini uygulama
  const statusCounts = useMemo(() => {
    const base = customerTypeFilter === 'all' ? tasks : tasks.filter(t =>
      customerTypeFilter === 'none' ? !t.customer_type : t.customer_type === customerTypeFilter
    );
    return {
      all: base.length,
      overdue: base.filter(t => t.is_overdue || t.status === 'overdue').length,
      in_progress: base.filter(t => t.status === 'in_progress' && !t.is_overdue && t.status !== 'overdue').length,
      completed: base.filter(t => t.status === 'completed').length,
    };
  }, [tasks, customerTypeFilter]);

  // Müşteri tipi sayıları — durum filtresini uygula, müşteri tipini uygulama
  const customerTypeCounts = useMemo(() => {
    const base = statusFilter === 'all' ? tasks : tasks.filter(t => {
      const isOverdue = t.is_overdue || t.status === 'overdue';
      if (statusFilter === 'overdue') return isOverdue;
      if (statusFilter === 'in_progress') return t.status === 'in_progress' && !isOverdue;
      if (statusFilter === 'completed') return t.status === 'completed';
      return true;
    });
    return {
      all: base.length,
      sanayici: base.filter(t => t.customer_type === 'sanayici').length,
      boru: base.filter(t => t.customer_type === 'boru').length,
      bayi: base.filter(t => t.customer_type === 'bayi').length,
      none: base.filter(t => !t.customer_type).length,
    };
  }, [tasks, statusFilter]);

  return (
    <div>
      {/* Sekmeler + filtreler + Yeni İş: kaydırma alanı Layout main içindeyse sticky ile main’in üstüne yapışır */}
      <div className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_rgb(243_244_246)]">
        {/* Sekmeler */}
        <div className="border-b border-gray-100 px-2 flex gap-1 overflow-x-auto no-scrollbar">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`whitespace-nowrap px-3 py-3 text-sm font-medium border-b-2 transition-colors
                ${activeTab === tab.key
                  ? 'border-brand-500 text-brand-600'
                  : 'border-transparent text-gray-500'
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Filtreler */}
        <div className="border-b border-gray-100 px-4 pt-3 pb-2 space-y-2">
          {/* Durum filtresi */}
          <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
            {STATUS_FILTERS.map(f => (
              <button
                key={f.key}
                type="button"
                onClick={() => { setStatusFilter(f.key); setCurrentPage(1); }}
                className={`whitespace-nowrap px-3 py-1 rounded-full text-xs font-medium border transition-colors shrink-0
                  ${statusFilter === f.key
                    ? f.key === 'overdue'
                      ? 'bg-red-500 text-white border-red-500'
                      : f.key === 'in_progress'
                      ? 'bg-amber-400 text-white border-amber-400'
                      : f.key === 'completed'
                      ? 'bg-green-500 text-white border-green-500'
                      : 'bg-brand-500 text-white border-brand-500'
                    : 'bg-white text-gray-500 border-gray-200'
                  }`}
              >
                {f.label} <span className="opacity-70">({statusCounts[f.key] ?? 0})</span>
              </button>
            ))}
          </div>

          {/* Müşteri tipi filtresi */}
          <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
            {CUSTOMER_TYPE_FILTERS.map(f => (
              <button
                key={f.key}
                type="button"
                onClick={() => { setCustomerTypeFilter(f.key); setCurrentPage(1); }}
                className={`whitespace-nowrap px-3 py-1 rounded-full text-xs font-medium border transition-colors shrink-0
                  ${customerTypeFilter === f.key
                    ? 'bg-slate-600 text-white border-slate-600'
                    : 'bg-white text-gray-500 border-gray-200'
                  }`}
              >
                {f.label} <span className="opacity-70">({customerTypeCounts[f.key] ?? 0})</span>
              </button>
            ))}
          </div>

          {/* Arama + Sorumlu filtresi */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none"
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
              <input
                type="text"
                placeholder="Kod, başlık veya müşteri…"
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                style={{ fontSize: '16px' }}
                className="w-full pl-7 pr-7 py-1.5 border border-gray-200 rounded-full bg-white focus:outline-none focus:ring-1 focus:ring-brand-400"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => { setSearchQuery(''); setCurrentPage(1); }}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600"
                  aria-label="Aramayı temizle"
                >
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} className="w-3.5 h-3.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            {assigneeOptions.length > 0 && (
              <select
                value={assigneeFilter}
                onChange={e => { setAssigneeFilter(e.target.value); setCurrentPage(1); }}
                className={`text-xs border rounded-full px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-400 shrink-0
                  ${assigneeFilter !== 'all' ? 'border-brand-500 text-brand-700 bg-brand-50' : 'border-gray-200 text-gray-500 bg-white'}`}
                aria-label={activeTab === 'delegated' ? 'Delegeye göre filtrele' : 'Sorumluya göre filtrele'}
              >
                <option value="all">{activeTab === 'delegated' ? 'Tüm delegeler' : 'Tüm sorumlular'}</option>
                {assigneeOptions.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Yeni İş butonu */}
        <div className="px-4 py-3 border-b border-gray-100">
          <button type="button" className="btn-primary" onClick={openNewTaskModal}>
            + Yeni İş
          </button>
        </div>
      </div>

      {/* Liste */}
      <div className="px-4 pt-2 pb-24">
        {loading && (
          <div className="flex justify-center py-12">
            <div className="w-7 h-7 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {!loading && !error && filteredTasks.length === 0 && (
          <div className="text-center py-16">
            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-7 h-7 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2" />
              </svg>
            </div>
            <p className="text-gray-400 text-sm">
              {activeFilterCount > 0
                ? 'Bu filtrelerle eşleşen iş yok.'
                : activeTab === 'mine' ? 'Henüz iş kaydı yok.'
                : activeTab === 'all' ? 'Hiç iş kaydı yok.'
                : 'Devrettiğiniz iş yok.'}
            </p>
            {activeFilterCount > 0 && (
              <button
                type="button"
                className="mt-2 text-xs text-brand-600 underline"
                onClick={() => { setStatusFilter('all'); setCustomerTypeFilter('all'); setAssigneeFilter('all'); setSearchQuery(''); }}
              >
                Filtreleri temizle
              </button>
            )}
          </div>
        )}

        {!loading && (() => {
          const totalPages = Math.ceil(filteredTasks.length / PAGE_SIZE);
          const pageTasks = filteredTasks.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
          return (
            <>
              {pageTasks.map((task) => (
                <TaskCard key={task.id} task={task} />
              ))}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-1 pt-3 pb-1 flex-wrap">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      type="button"
                      onClick={() => setCurrentPage(page)}
                      className={`w-8 h-8 rounded-full text-xs font-semibold transition-colors
                        ${currentPage === page
                          ? 'bg-brand-500 text-white'
                          : 'bg-white text-gray-500 border border-gray-200 hover:border-brand-400 hover:text-brand-600'
                        }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
              )}
            </>
          );
        })()}
      </div>

      {newTaskOpen && (
        <div className="fixed inset-0 z-[80] pointer-events-none">
          <button
            type="button"
            aria-label="Formu kapat"
            className="absolute inset-0 bg-black/40 pointer-events-auto"
            onClick={closeNewTaskModal}
          />
          <aside className="absolute right-0 top-0 h-full w-full max-w-md bg-gray-50 shadow-[-8px_0_24px_rgba(0,0,0,0.12)] pointer-events-auto flex flex-col border-l border-gray-200">
            <div className="shrink-0 flex justify-end px-4 py-2 border-b border-gray-200 bg-white">
              <button
                type="button"
                className="text-sm font-medium text-brand-600 py-1 px-2 -mr-2"
                onClick={closeNewTaskModal}
              >
                Kapat
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain pb-6">
              <NewTaskPage
                onSuccess={() => {
                  closeNewTaskModal();
                  fetchTasks();
                }}
                onCancel={closeNewTaskModal}
              />
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
